import React, { useEffect, useState } from "react";
import { Loader2, Upload, X, Star } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { useToast } from "../hooks/use-toast";
import { adminCreateReview, adminUpdateReview, adminUploadImage, fileUrl } from "../api";
import { compressImage } from "../lib/compressImage";

const EMPTY = { name: "", rating: 5, text: "", has_photo: false, photo: "" };

export const AdminReviewDialog = ({ open, onClose, token, review, onSaved }) => {
  const { toast } = useToast();
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (review) {
      setForm({
        name: review.name || "", rating: review.rating || 5, text: review.text || "",
        has_photo: !!review.has_photo, photo: review.photo || "",
      });
    } else {
      setForm(EMPTY);
    }
  }, [review, open]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleUpload = async (e) => {
    const f = (e.target.files || [])[0];
    if (!f) return;
    setUploading(true);
    try {
      const compressed = await compressImage(f);
      const res = await adminUploadImage(compressed, token);
      setForm((p) => ({ ...p, photo: res.url }));
    } catch (err) {
      toast({ title: "Upload mislukt", description: err?.response?.data?.detail || "Probeer een andere afbeelding." });
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim() || !form.text.trim()) { toast({ title: "Vul naam en review in" }); return; }
    setSaving(true);
    const payload = {
      name: form.name.trim(), rating: Number(form.rating) || 5, text: form.text.trim(),
      has_photo: form.has_photo, photo: form.has_photo ? form.photo : "",
    };
    try {
      if (review) await adminUpdateReview(review.id, payload, token);
      else await adminCreateReview(payload, token);
      toast({ title: review ? "Review bijgewerkt" : "Review toegevoegd" });
      onSaved();
      onClose();
    } catch (err) {
      toast({ title: "Opslaan mislukt", description: err?.response?.data?.detail || "Probeer opnieuw." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="review-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl text-brand-navy">{review ? "Review bewerken" : "Nieuwe review"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="font-semibold text-brand-navy">Naam klant *</Label>
            <Input data-testid="review-name-input" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="bv. Sanne uit Utrecht" />
          </div>

          <div>
            <Label className="font-semibold text-brand-navy">Sterren</Label>
            <div className="flex items-center gap-1 mt-2" data-testid="review-rating-stars">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => set("rating", n)} data-testid={`review-star-${n}`} className="p-0.5 transition-transform hover:scale-110">
                  <Star className={`w-7 h-7 ${n <= form.rating ? "fill-brand-yellow text-brand-yellow" : "text-slate-300"}`} />
                </button>
              ))}
              <span className="ml-2 text-sm font-semibold text-slate-500">{form.rating} / 5</span>
            </div>
          </div>

          <div>
            <Label className="font-semibold text-brand-navy">Review tekst *</Label>
            <Textarea data-testid="review-text-input" className="mt-1.5" rows={4} value={form.text} onChange={(e) => set("text", e.target.value)} placeholder="Wat schreef de klant over jullie?" />
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
            <div>
              <Label className="font-semibold text-brand-navy">Klantfoto toevoegen</Label>
              <p className="text-xs text-slate-500 mt-0.5">Toon een foto bij de review.</p>
            </div>
            <Switch checked={form.has_photo} onCheckedChange={(v) => set("has_photo", v)} data-testid="review-photo-switch" />
          </div>

          {form.has_photo && (
            <div className="flex items-center gap-3">
              {form.photo ? (
                <div className="relative w-20 h-20 rounded-full overflow-hidden border border-slate-200">
                  <img src={fileUrl(form.photo)} alt="" className="w-full h-full object-cover" />
                  <button type="button" onClick={() => set("photo", "")} data-testid="review-remove-photo" className="absolute top-0 right-0 w-6 h-6 rounded-full bg-white text-brand-pink flex items-center justify-center shadow"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <label className="w-20 h-20 rounded-full border-2 border-dashed border-slate-300 flex flex-col items-center justify-center cursor-pointer text-slate-400 hover:border-brand-blue hover:text-brand-blue transition-colors" data-testid="review-upload-label">
                  {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Upload className="w-5 h-5" />}
                  <span className="text-[10px] mt-1">Foto</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleUpload} data-testid="review-photo-input" />
                </label>
              )}
              <p className="text-xs text-slate-500">Vierkante foto werkt het mooist.</p>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="flex-1 rounded-full border-2 border-slate-200 text-brand-navy font-bold py-3 hover:bg-slate-50 transition-colors">Annuleren</button>
            <button onClick={save} disabled={saving || uploading} data-testid="review-save-button" className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brand-blue hover:bg-brand-bluedark text-white font-bold py-3 transition-colors disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} {review ? "Opslaan" : "Toevoegen"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AdminReviewDialog;
